import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle, TrendingUp, Lock, EyeOff, BrainCircuit, Globe, Users, PieChart, KeyRound } from 'lucide-react';

const FEATURES = [
  { icon: TrendingUp, color: 'bg-emerald-100 text-emerald-600', title: '14 Investment Classes, One Dashboard', desc: 'Track Mutual Funds, Treasury Bills, Bonds, Quoted Stocks, and 10 more investment classes in one unified dashboard.' },
  { icon: Users, color: 'bg-rose-100 text-rose-600', title: 'Multi-Institutional Tracking', desc: 'Track investments across multiple institutions and governments for complete portfolio visibility.' },
  {
    icon: Lock,
    color: 'bg-emerald-100 text-emerald-700',
    title: 'Encrypted So Even We Can\u2019t See It',
    desc: 'Your amounts, names and private notes are scrambled on your device before they ever reach our servers. Only your password can unscramble them \u2014 and we don\u2019t have your password.',
    checks: [
      'Sensitive data encrypted on your device, before it leaves',
      'We never see your figures, ever \u2014 even our engineers can\u2019t read them',
      'Two-factor sign-in and always-on Auto-Lock when you step away',
    ],
  },
  { icon: KeyRound, color: 'bg-teal-100 text-teal-700', hero: true, title: 'Continuity System, By Design', desc: 'Building wealth is only half the story. The Continuity System makes sure the other half \u2014 who knows about it, and how \u2014 is quietly settled long before it matters.', checks: ['Your asset details, in plain language', 'Trusted Contacts you appoint yourself', 'Encrypted uploads that unlock only when we confirm'] },
  {
    icon: EyeOff,
    color: 'bg-emerald-100 text-emerald-600',
    title: 'Invisible Everywhere You Go',
    desc: 'One shield for public places. A code name in place of your real name. One tap to hide every figure. Anyone glancing at your screen sees nothing recognisable.',
    checks: [
      'Privacy Mode \u2014 one shield hides everything at once',
      'Code Names \u2014 your real name never appears anywhere in the app',
      'Browser Disguise \u2014 so anyone glancing at your screen sees nothing recognisable',
    ],
  },
  {
    icon: BrainCircuit,
    color: 'bg-rose-100 text-rose-600',
    title: 'No AI on Your Data. Ever.',
    desc: 'While other platforms race to pipe your holdings into AI, we do the opposite. A hard-wired policy inside our code blocks any AI or LLM from ever touching your portfolio. Not for suggestions, not for reports, not for anything.',
    checks: [
      'Code-level enforcement \u2014 not just a promise on a page',
      'Zero third-party AI integrations, by design',
      'Your data trains nothing, powers nothing outside your Continuity System',
    ],
  },
  { icon: Globe, color: 'bg-blue-100 text-blue-600', title: 'Global Currency Support', desc: 'Support for 150+ currencies for investments anywhere in the world.' },
  { icon: PieChart, color: 'bg-purple-100 text-purple-600', title: 'Portfolio Allocation & Performance Tracking', desc: 'Get detailed portfolio insights, allocation breakdowns, performance metrics, and risk analysis to make informed decisions.' },
];

const FeaturesSection = () => (
  <section id="features" className="container mx-auto px-4 sm:px-6 py-12 sm:py-24">
    <div className="text-center space-y-4 mb-8 sm:mb-16">
      <div className="inline-flex flex-col items-center gap-2">
        <div className="subheading">Features</div>
        <span aria-hidden className="block w-16 h-0.5 bg-linear-to-r from-emerald-500 to-blue-500 rounded-full"></span>
      </div>
      <h2 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900">
        Built for <span className="text-emerald-600">clarity</span> today. Engineered for <span className="text-blue-600">continuity</span> tomorrow.
      </h2>
      <p className="body-text max-w-2xl mx-auto">14 asset classes. Multiple institutions. 150+ currencies. Secured, private, and unified in one independent view.</p>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-8">
      {FEATURES.map((f) => {
        const Icon = f.icon;
        if (f.hero) {
          return (
            <Card key={f.title} className="portfolio-card md:col-span-3 bg-linear-to-br from-teal-50/60 via-white to-white border-teal-200 relative" data-testid="feature-card-continuity-hero">
              <CardHeader>
                <div className="md:grid md:grid-cols-3 md:gap-10">
                  <div className="md:col-span-1">
                    <div className={`w-14 h-14 ${f.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4`}>
                      <Icon className={`w-7 h-7 ${f.color.split(' ')[1]}`} />
                    </div>
                    <CardTitle className="text-2xl font-serif">{f.title}</CardTitle>
                    <CardDescription className="body-text mt-3">{f.desc}</CardDescription>
                  </div>
                  <div className="md:col-span-2 md:pl-8 md:border-l md:border-teal-200 mt-6 md:mt-0 flex md:items-center">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
                      {f.checks.map(c => (
                        <div key={c} className="flex items-start space-x-2">
                          <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5 shrink-0" />
                          <span className="text-sm text-slate-700 font-medium leading-snug">{c}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardHeader>
            </Card>
          );
        }
        return (
          <Card key={f.title} className="portfolio-card">
            <CardHeader>
              <div className={`w-12 h-12 ${f.color.split(' ')[0]} rounded-xl flex items-center justify-center mb-4`}>
                <Icon className={`w-6 h-6 ${f.color.split(' ')[1]}`} />
              </div>
              <CardTitle className="text-xl font-serif">{f.title}</CardTitle>
              <CardDescription className="body-text">{f.desc}</CardDescription>
              {f.checks && (
                <div className="mt-4 space-y-2">
                  {f.checks.map(c => (
                    <div key={c} className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span className="text-sm text-slate-600">{c}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardHeader>
          </Card>
        );
      })}
    </div>
  </section>
);

export default FeaturesSection;
